import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg } from "../core/api";
import { useApp } from "../core/AppContext";
import { fa, formatDate } from "../core/format";
import Modal from "./Modal";
import FileViewer from "./FileViewer";

const OK = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
const MAX_MB = 5;
const sizeText = (b) =>
  b >= 1048576
    ? `${fa((b / 1048576).toFixed(1))} مگابایت`
    : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`;
const icon = (m) => (m === "application/pdf" ? "fa-regular fa-file-pdf" : "fa-regular fa-image");

function FileRow({ f, onView, onRemove }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", background: "var(--card)" }}>
      <i className={icon(f.mime)} style={{ color: "var(--primary)" }} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{f.name}</span>
      <small className="hint" style={{ margin: 0 }}>{sizeText(f.size)}</small>
      <button type="button" className="icon-btn" title="مشاهده" onClick={() => onView(f)}><i className="fa-regular fa-eye" /></button>
      {onRemove && <button type="button" className="icon-btn danger" title="حذف" onClick={() => onRemove(f)}><i className="fa-solid fa-trash" /></button>}
    </div>
  );
}

/** مودال مشاهده‌ی خبر برای همه‌ی کاربران (admin=true: امکان افزودن/حذف فایل) */
export function NewsModal({ id, admin = false, onClose, onChanged }) {
  const { toast, confirm } = useApp();
  const inputRef = useRef(null);
  const [item, setItem] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  const base = `/announcements/${id}/files`;

  const load = useCallback(async () => {
    try {
      const [a, f] = await Promise.all([api.get(`/announcements/${id}`), api.get(base)]);
      setItem(a.data.item);
      setFiles(f.data.items);
    } catch (e) {
      toast(errMsg(e), "err");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [id, base, toast, onClose]);
  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = "";
    if (!picked.length) return;
    const bad = picked.find((f) => !OK.includes(f.type) || f.size > MAX_MB * 1024 * 1024);
    if (bad) return toast(`«${bad.name}»: فقط PDF یا تصویر تا ${fa(MAX_MB)} مگابایت مجاز است`, "err");
    setBusy(true);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append("files", f));
      const { data } = await api.post(base, fd, { timeout: 180000 });
      setFiles(data.items);
      toast("فایل‌ها ذخیره شد");
      onChanged?.();
    } catch (er) {
      toast(errMsg(er), "err");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!(await confirm(`فایل «${f.name}» حذف شود؟`))) return;
    try {
      await api.delete(`${base}/${f.id}`);
      setFiles((l) => l.filter((x) => x.id !== f.id));
      onChanged?.();
    } catch (er) {
      toast(errMsg(er), "err");
    }
  };

  return (
    <>
      <Modal wide title={item?.title || "خبر"} onClose={() => !busy && !viewer && onClose()}
        footer={<button className="btn btn-ghost" onClick={onClose} disabled={busy}>بستن</button>}>
        {loading ? (
          <div className="empty"><div className="spinner" style={{ margin: "0 auto" }} /></div>
        ) : (
          <>
            <div className="hint" style={{ marginBottom: 10 }}>
              <i className="fa-regular fa-calendar" /> {formatDate(item.publishDate)}
            </div>
            {item.body ? (
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 2, background: "var(--card-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                {item.body}
              </div>
            ) : (
              <div className="hint" style={{ marginBottom: 14 }}>متنی ثبت نشده است.</div>
            )}

            {(files.length > 0 || admin) && (
              <div className="field">
                <div className="box-head" style={{ marginBottom: 8 }}>
                  <h3 style={{ fontSize: 14 }}>فایل‌های پیوست</h3>
                  {admin && (
                    <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => inputRef.current.click()}>
                      <i className="fa-solid fa-plus" /> {busy ? "در حال ارسال..." : "افزودن فایل"}
                    </button>
                  )}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {files.map((f) => (
                    <FileRow key={f.id} f={f}
                      onView={(x) => setViewer({ path: `${base}/${x.id}`, name: x.name })}
                      onRemove={admin ? remove : null} />
                  ))}
                  {!files.length && <div className="hint">فایلی پیوست نشده است.</div>}
                </div>
                {admin && (
                  <>
                    <div className="hint" style={{ marginTop: 8 }}>تا {fa(10)} فایل، هر فایل حداکثر {fa(MAX_MB)} مگابایت (PDF یا تصویر).</div>
                    <input ref={inputRef} type="file" multiple hidden accept="application/pdf,image/png,image/jpeg,image/webp" onChange={onPick} />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
    </>
  );
}

export default NewsModal;